import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';

function Sujets() {
  const INITIAL_MANUAL_FORM = {
    titre: '',
    mot_cle: '',
    description: '',
  };
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [sujets, setSujets] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingSujets, setLoadingSujets] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualForm, setManualForm] = useState(INITIAL_MANUAL_FORM);
  const [error, setError] = useState('');

  const loadSites = async () => {
    try {
      setError('');
      setLoadingSites(true);
      const response = await apiFetch('/api/sites');
      if (!response.ok) throw new Error('Erreur chargement sites');
      const data = await response.json();
      const nextSites = Array.isArray(data) ? data : [];
      setSites(nextSites);
      if (nextSites.length > 0) {
        setSelectedSiteId((current) => current || nextSites[0].id);
      }
    } catch (requestError) {
      setError('Impossible de charger les sites.');
    } finally {
      setLoadingSites(false);
    }
  };

  const loadSujets = async (siteId) => {
    if (!siteId) {
      setSujets([]);
      return;
    }

    try {
      setError('');
      setLoadingSujets(true);
      const response = await apiFetch(`/api/sujets/${siteId}`);
      if (!response.ok) throw new Error('Erreur chargement sujets');
      const data = await response.json();
      setSujets(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError('Impossible de charger les sujets.');
    } finally {
      setLoadingSujets(false);
    }
  };

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    loadSujets(selectedSiteId);
  }, [selectedSiteId]);

  const generateSujets = async () => {
    if (!selectedSiteId) return;

    try {
      setError('');
      setGenerating(true);
      const response = await apiFetch(`/api/sujets/proposer/${selectedSiteId}`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Erreur generation sujets');
      await loadSujets(selectedSiteId);
    } catch (requestError) {
      setError('Impossible de générer les sujets.');
    } finally {
      setGenerating(false);
    }
  };

  const updateSujetStatus = async (sujetId, statut) => {
    try {
      setError('');
      setLoadingId(sujetId);
      const response = await apiFetch(`/api/sujets/${sujetId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut }),
      });
      if (!response.ok) throw new Error('Erreur mise a jour sujet');
      const updatedSujet = await response.json();
      setSujets((current) => current.map((sujet) => (sujet.id === sujetId ? updatedSujet : sujet)));
    } catch (requestError) {
      setError('Impossible de mettre à jour ce sujet.');
    } finally {
      setLoadingId(null);
    }
  };

  const openManualModal = () => {
    setError('');
    setManualForm(INITIAL_MANUAL_FORM);
    setIsManualModalOpen(true);
  };

  const closeManualModal = () => {
    setIsManualModalOpen(false);
  };

  const handleManualChange = (event) => {
    const { name, value } = event.target;
    setManualForm((current) => ({ ...current, [name]: value }));
  };

  const handleManualSubmit = async (event) => {
    event.preventDefault();
    if (!selectedSiteId) return;

    try {
      setError('');
      setManualSubmitting(true);
      const response = await apiFetch('/api/sujets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: selectedSiteId,
          titre: manualForm.titre,
          mot_cle: manualForm.mot_cle,
          description: manualForm.description,
          statut: 'validé',
        }),
      });
      if (!response.ok) throw new Error('Erreur creation sujet manuel');
      const created = await response.json();
      setSujets((current) => [created, ...current]);
      closeManualModal();
    } catch (requestError) {
      setError('Impossible de créer le sujet manuellement.');
    } finally {
      setManualSubmitting(false);
    }
  };

  const sujetsValides = useMemo(
    () => sujets.filter((sujet) => sujet.statut === 'validé'),
    [sujets]
  );
  const sujetsProposes = useMemo(
    () => sujets.filter((sujet) => sujet.statut === 'proposé'),
    [sujets]
  );

  return (
    <section className="sujets-page">
      <header className="sujets-header">
        <div>
          <h1>Proposition de sujets</h1>
          <p>Générez des idées d'articles SEO et validez les meilleures opportunités.</p>
        </div>
        <div className="motscles-header-actions">
          <button
            type="button"
            className="secondary-button sujets-generate-button"
            onClick={openManualModal}
            disabled={!selectedSiteId}
          >
            Créer un sujet manuellement
          </button>
          <button
            type="button"
            className="primary-button sujets-generate-button"
            onClick={generateSujets}
            disabled={!selectedSiteId || generating}
          >
            {generating ? 'Génération en cours...' : 'Générer des sujets'}
          </button>
        </div>
      </header>

      {error && <p className="sites-error">{error}</p>}

      <div className="sujets-toolbar">
        <label htmlFor="siteSelect">Site à travailler</label>
        <select
          id="siteSelect"
          value={selectedSiteId}
          onChange={(event) => setSelectedSiteId(event.target.value)}
          disabled={loadingSites || sites.length === 0}
        >
          {sites.length === 0 ? (
            <option value="">Aucun site disponible</option>
          ) : (
            sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.nom}
              </option>
            ))
          )}
        </select>
      </div>

      <section className="sujets-section">
        <h2>Sujets proposés</h2>
        {loadingSujets ? (
          <div className="sites-empty-card">
            <p>Chargement des sujets...</p>
          </div>
        ) : sujetsProposes.length === 0 ? (
          <div className="sites-empty-card">
            <p>Aucun sujet proposé pour ce site.</p>
          </div>
        ) : (
          <div className="sujets-grid">
            {sujetsProposes.map((sujet) => (
              <article key={sujet.id} className="sujet-card">
                <h3>{sujet.titre}</h3>
                <p className="sujet-keyword">Mot-clé : {sujet.motCle}</p>
                <p className="sujet-description">{sujet.description || 'Sans description.'}</p>
                <div className="sujet-actions">
                  <button
                    type="button"
                    className="sujet-validate-button"
                    onClick={() => updateSujetStatus(sujet.id, 'validé')}
                    disabled={loadingId === sujet.id}
                  >
                    Valider
                  </button>
                  <button
                    type="button"
                    className="sujet-refuse-button"
                    onClick={() => updateSujetStatus(sujet.id, 'refusé')}
                    disabled={loadingId === sujet.id}
                  >
                    Refuser
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="sujets-section">
        <h2>Sujets validés</h2>
        {loadingSujets ? null : sujetsValides.length === 0 ? (
          <div className="sites-empty-card">
            <p>Aucun sujet validé pour le moment.</p>
          </div>
        ) : (
          <div className="sujets-grid">
            {sujetsValides.map((sujet) => (
              <article key={sujet.id} className="sujet-card sujet-card-validated">
                <h3>{sujet.titre}</h3>
                <p className="sujet-keyword">Mot-clé : {sujet.motCle}</p>
                <p className="sujet-description">{sujet.description || 'Sans description.'}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      {isManualModalOpen && (
        <div className="sites-modal-overlay" role="presentation" onClick={closeManualModal}>
          <div
            className="sites-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Créer un sujet manuellement"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="sites-modal-header">
              <h2>Créer un sujet manuellement</h2>
              <button type="button" className="sites-close-button" onClick={closeManualModal}>
                ×
              </button>
            </header>
            <form className="sites-form" onSubmit={handleManualSubmit}>
              <label htmlFor="manual-titre">Titre du sujet</label>
              <input
                id="manual-titre"
                name="titre"
                value={manualForm.titre}
                onChange={handleManualChange}
                required
              />

              <label htmlFor="manual-motcle">Mot-clé principal</label>
              <input
                id="manual-motcle"
                name="mot_cle"
                value={manualForm.mot_cle}
                onChange={handleManualChange}
                required
              />

              <label htmlFor="manual-description">Description courte</label>
              <input
                id="manual-description"
                name="description"
                value={manualForm.description}
                onChange={handleManualChange}
              />

              <div className="sites-form-actions">
                <button type="button" className="secondary-button" onClick={closeManualModal}>
                  Annuler
                </button>
                <button type="submit" className="primary-button" disabled={manualSubmitting}>
                  {manualSubmitting ? 'Création...' : 'Créer (validé)'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

export default Sujets;
